import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Toggl-style call timer.
 *
 * Lives in a global Zustand store so the elapsed time keeps ticking
 * across page navigations — the user can start a call from a company
 * detail page, browse to the dashboard, and the banner / pill stays
 * live throughout. Persisted to localStorage so a tab refresh
 * doesn't lose the in-flight call (the wall clock keeps ticking
 * because we store an absolute `startedAtMs`, not the elapsed value).
 *
 * Two phases:
 *   - active=true while the call is running
 *   - active=false + pendingLog set briefly between Stop and the user
 *     submitting / dismissing the LogCallDialog. The CompanyDetailPage
 *     watches for pendingLog and opens its dialog pre-filled with
 *     the timer's snapshot (contact, duration, started/ended times).
 *
 * Dates are stored as ms-since-epoch instead of `Date` instances so
 * the persist middleware's default JSON (de)serialisation round-trips
 * cleanly without a custom replacer/reviver.
 */

export interface CallTimerSnapshot {
  /** Project this call belongs to — needed by the LogCallDialog +
   *  navigation back to the company page when stopping from a
   *  different surface. */
  slug: string;
  companyId: number;
  companyName: string;
  contactId: number | null;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
}

export interface PendingLogPayload extends CallTimerSnapshot {
  startedAtMs: number;
  endedAtMs: number;
  /** Rounded UP to the nearest minute — a 17-second call still shows
   *  as 1 minute, which matches what users expect to see. */
  durationMinutes: number;
}

interface CallTimerState extends Partial<CallTimerSnapshot> {
  active: boolean;
  startedAtMs: number | null;
  pendingLog: PendingLogPayload | null;

  startCall: (snapshot: CallTimerSnapshot) => void;
  /** Stops the timer and stages a `pendingLog` payload for whichever
   *  surface listens for it (CompanyDetailPage opens the
   *  LogCallDialog when the pending company matches). */
  stopCall: () => void;
  /** Cancel without staging a pending log — used by the "discard"
   *  paths (tab close warning, dialog cancel without submit). */
  cancelCall: () => void;
  /** Clears `pendingLog` once the LogCallDialog has been shown +
   *  resolved (submit OR cancel). */
  clearPendingLog: () => void;
}

const EMPTY_TIMER = {
  active: false as const,
  slug: undefined,
  companyId: undefined,
  companyName: undefined,
  contactId: undefined,
  contactName: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  startedAtMs: null,
};

export const useCallTimer = create<CallTimerState>()(
  persist(
    (set, get) => ({
      ...EMPTY_TIMER,
      pendingLog: null,
      startCall: (snapshot) =>
        set({
          active: true,
          startedAtMs: Date.now(),
          pendingLog: null,
          ...snapshot,
        }),
      stopCall: () => {
        const s = get();
        if (!s.active || s.startedAtMs == null || !s.slug || s.companyId == null) {
          set({ ...EMPTY_TIMER });
          return;
        }
        const endedAtMs = Date.now();
        const elapsedMs = Math.max(0, endedAtMs - s.startedAtMs);
        const durationMinutes = Math.max(
          1,
          Math.ceil(elapsedMs / 60_000),
        );
        const pendingLog: PendingLogPayload = {
          slug: s.slug,
          companyId: s.companyId,
          companyName: s.companyName ?? "",
          contactId: s.contactId ?? null,
          contactName: s.contactName ?? "",
          contactEmail: s.contactEmail ?? null,
          contactPhone: s.contactPhone ?? null,
          startedAtMs: s.startedAtMs,
          endedAtMs,
          durationMinutes,
        };
        set({ ...EMPTY_TIMER, pendingLog });
      },
      cancelCall: () => set({ ...EMPTY_TIMER, pendingLog: null }),
      clearPendingLog: () => set({ pendingLog: null }),
    }),
    { name: "prism.calltimer" },
  ),
);

/** Format a non-negative seconds value as MM:SS (or HH:MM:SS past
 *  the hour). */
export function formatCallDuration(totalSeconds: number): string {
  const t = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}
